import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import CollectionFlipCards from "./CollectionFlipCards";

export default function Hero() {
  const [topOffset, setTopOffset] = useState(0);

  useEffect(() => {
    const updateTopOffset = () => {
      const announcementBar = document.querySelector('[data-announcement-bar]');
      const header = document.querySelector('header');
      const announcementHeight = announcementBar ? announcementBar.clientHeight : 0;
      const headerHeight = header ? header.clientHeight : 80;
      setTopOffset(announcementHeight + headerHeight);
    };
    updateTopOffset();
    const interval = setInterval(updateTopOffset, 100);
    return () => clearInterval(interval);
  }, []);

  return (
    <section 
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-aarsa-cream-200 via-aarsa-cream-100 to-aarsa-sage-50"
      style={{ paddingTop: `${topOffset}px` }}
    >
      {/* Base Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-aarsa-cream-200 via-aarsa-gold-50 to-aarsa-sage-100"></div>
      
      {/* Animated Background Elements with Glows */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Golden Glow 1 */}
        <motion.div
          className="absolute top-20 left-10 w-96 h-96 bg-aarsa-gold-300 rounded-full mix-blend-multiply filter blur-3xl opacity-40"
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        {/* Sage Glow */}
        <motion.div
          className="absolute top-40 right-10 w-96 h-96 bg-aarsa-sage-300 rounded-full mix-blend-multiply filter blur-3xl opacity-40"
          animate={{
            x: [0, -100, 0],
            y: [0, 50, 0],
            scale: [1, 1.3, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        {/* Golden Glow 2 */}
        <motion.div
          className="absolute bottom-20 left-1/2 w-96 h-96 bg-aarsa-gold-400 rounded-full mix-blend-multiply filter blur-3xl opacity-35"
          animate={{
            x: [0, 50, 0],
            y: [0, -100, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        {/* Additional Glow Elements */}
        <motion.div
          className="absolute top-1/2 left-1/4 w-80 h-80 bg-aarsa-golden-light rounded-full mix-blend-multiply filter blur-2xl opacity-30"
          animate={{
            x: [0, 80, 0],
            y: [0, -80, 0],
            scale: [1, 1.15, 1],
          }}
          transition={{
            duration: 22,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute bottom-1/3 right-1/4 w-72 h-72 bg-aarsa-sage-400 rounded-full mix-blend-multiply filter blur-2xl opacity-30"
          animate={{
            x: [0, -60, 0],
            y: [0, 70, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 28,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>
      
      {/* Overlay Gradient for Depth */}
      <div className="absolute inset-0 bg-gradient-to-t from-transparent via-transparent to-aarsa-cream-100/30"></div>

      {/* Content Container */}
      <div className="relative z-10 container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center min-h-screen py-12">
          {/* Left Section - Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-aarsa-gold-200"
            >
              <Sparkles className="w-4 h-4 text-aarsa-gold-600" />
              <span className="text-sm font-medium text-aarsa-gold-600">
                New Collection Available
              </span>
            </motion.div>

            {/* Main Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold font-playfair leading-tight"
            >
              <span className="text-gray-900">Wear your </span>
              <span className="gradient-text">Worth</span>
            </motion.h1>

            {/* Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="text-base md:text-lg text-gray-600 max-w-xl leading-relaxed space-y-3"
            >
              <p>
                Each piece is carefully crafted to celebrate your unique style,
                whether it's for a special occasion or everyday moments.
                Discover collections that blend traditional artistry with
                contemporary designs, made to make you feel extraordinary.
              </p>
            </motion.div>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="pt-4"
            >
              <Link to="/products" className="inline-block">
                <motion.button
                  type="button"
                  className="group relative flex items-center space-x-2 px-8 py-4 rounded-full bg-gradient-to-r from-aarsa-sage-600 to-aarsa-sage-700 text-white font-semibold text-base shadow-lg hover:shadow-xl hover:from-aarsa-sage-700 hover:to-aarsa-sage-800 transition-all cursor-pointer overflow-hidden"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Shine Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  
                  {/* Content */}
                  <span className="relative z-10">Explore Collection</span>
                  <ArrowRight 
                    className="w-5 h-5 relative z-10 transition-transform duration-300 group-hover:translate-x-1" 
                  />
                </motion.button>
              </Link>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="grid grid-cols-3 gap-8 pt-8"
            >
              {[
                { number: "22K", label: "Happy Customers" },
                { number: "10K+", label: "Products" },
                { number: "4.9★", label: "Average Rating" },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1 + index * 0.1 }}
                  className="text-left"
                >
                  <div className="text-xl md:text-2xl font-bold gradient-text">
                    {stat.number}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Section - Flipping Cards */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="hidden lg:flex items-center justify-center"
          >
            <CollectionFlipCards />
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-gray-400 rounded-full flex items-start justify-center p-2"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 h-3 bg-gray-400 rounded-full"
          />
        </motion.div>
      </motion.div>
    </section>
  );
}
